import { useEffect, useState } from "react";
import { ArrowRight, Star } from "lucide-react";
import { TESTIMONIALS, GOOGLE_REVIEWS_URL } from "@/data/site";
import { Reveal } from "@/components/Reveal";
import { api } from "@/lib/api";

const Stars = ({ n = 5, className = "w-4 h-4" }) => (
  <span className="flex items-center gap-1 text-terra" aria-label={`${n} étoiles sur 5`}>
    {Array.from({ length: 5 }).map((_, i) => <Star key={i} className={`${className} ${i < Math.round(n) ? "fill-terra" : "opacity-30"}`} />)}
  </span>
);

const STATIC = TESTIMONIALS.map((t) => ({ auteur: t.auteur, texte: t.texte, note: 5, quand: t.contexte }));

export const GoogleReviews = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.avis().then(setData).catch(() => setData({ source: "statique", avis: [] }));
  }, []);

  const live = data?.source === "google" && data.avis?.length > 0;
  const avis = live ? data.avis : STATIC;
  const url = (live && data.url) || GOOGLE_REVIEWS_URL;

  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32" data-testid="testimonials" data-source={live ? "google" : "statique"}>
      <Reveal>
        <div className="flex flex-wrap items-center gap-4 mb-6">
          <p className="eyebrow">Avis Google</p>
          <span className="flex items-center gap-2" data-testid="reviews-rating">
            <Stars n={live ? data.note || 5 : 5} />
            <span className="font-mono text-xs text-fumee ml-1 tracking-widest">
              {live ? `${String(data.note ?? 5).replace(".", ",")} / 5` : "5,0 / 5"}{live && data.nombre ? ` · ${data.nombre} avis` : ""}
            </span>
          </span>
        </div>
        <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-bark leading-tight max-w-2xl mb-16">
          Ils et elles sont passé·e·s <em className="not-italic text-terra">entre les mondes.</em>
        </h2>
      </Reveal>
      <div className="grid md:grid-cols-2 gap-x-14 gap-y-12">
        {avis.map((t, i) => (
          <Reveal key={`${t.auteur}-${i}`} delay={i * 0.07}>
            <blockquote className="border-l-2 border-terra/50 pl-6" data-testid={`testimonial-${i}`}>
              <Stars n={t.note || 5} className="w-3.5 h-3.5" />
              <p className="font-serif text-xl lg:text-2xl text-bark leading-relaxed italic mt-3">« {t.texte} »</p>
              <footer className="mt-4 font-mono text-xs uppercase tracking-[0.2em] text-fumee flex flex-wrap items-center gap-2">
                {t.photo && <img src={t.photo} alt="" className="w-6 h-6 rounded-full" loading="lazy" referrerPolicy="no-referrer" />}
                {t.auteur_url ? <a href={t.auteur_url} target="_blank" rel="noopener noreferrer" className="hover:text-terra">{t.auteur}</a> : <span>{t.auteur}</span>}
                <span>· Avis Google</span>
                {t.quand && <span className="text-terra">— {t.quand}</span>}
              </footer>
            </blockquote>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.1}>
        <div className="flex flex-wrap items-center justify-between gap-4 mt-12">
          <a href={url} target="_blank" rel="noopener noreferrer" data-testid="google-reviews-link" className="inline-flex items-center gap-2 text-sm text-terra link-line">
            Lire tous les avis sur Google <ArrowRight className="w-4 h-4" />
          </a>
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-fumee/70" data-testid="reviews-attribution">
            {live ? `Avis fournis par Google Maps · ${data.ordre}` : "Sélection d'avis publiés sur Google Maps"}
          </p>
        </div>
      </Reveal>
    </section>
  );
};
